import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import api, { API_BASE } from "@/lib/api";
import { useMediaTicket, mediaUrl } from "@/lib/mediaTicket";
import { decodeAudioFromUrl, toMono, sliceSamples, concatSegments, exportClip } from "@/lib/audioEditor";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionLoader, EmptyState } from "@/components/common";
import {
  ArrowLeft, Play, Pause, Scissors, Loader2, Download, Trash2, Link2, Users2,
  Globe, Lock, AudioLines, UserRound,
} from "lucide-react";


function fmt(sec) {
  if (!isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const SHARE_META = {
  core: { label: "Core only", icon: Lock },
  members: { label: "Members", icon: Users2 },
  public: { label: "Public", icon: Globe },
};

export default function ClipEditor() {
  const { id } = useParams();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clips, setClips] = useState([]);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [region, setRegion] = useState(null);
  const [title, setTitle] = useState("");
  const [format, setFormat] = useState("mp3");
  const [saving, setSaving] = useState(false);

  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const regionsRef = useRef(null);
  const decodedRef = useRef(null);

  const ticket = useMediaTicket();
  const audioUrl = mediaUrl(`${API_BASE}/recordings/${id}/audio`, ticket);

  const loadData = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        api.get(`/recordings/${id}`).then((x) => x.data),
        api.get(`/recordings/${id}/clips`).then((x) => x.data).catch(() => []),
      ]);
      setRec(r);
      setClips(c);
    } catch (e) {
      toast.error("Could not load recording");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Init wavesurfer once we know there is audio
  useEffect(() => {
    if (loading || !rec?.has_audio || !audioUrl || !containerRef.current || wsRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      url: audioUrl,
      height: 96,
      waveColor: "#9CC4BF",
      progressColor: "#082D50",
      cursorColor: "#F59E7A",
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
    });
    const regions = ws.registerPlugin(RegionsPlugin.create());
    wsRef.current = ws;
    regionsRef.current = regions;
    ws.on("ready", () => setReady(true));
    ws.on("play", () => setPlaying(true));
    ws.on("pause", () => setPlaying(false));
    ws.on("finish", () => setPlaying(false));
    regions.on("region-created", (r) => {
      regions.getRegions().forEach((other) => { if (other.id !== r.id) other.remove(); });
      setRegion({ start: r.start, end: r.end });
    });
    regions.on("region-updated", (r) => setRegion({ start: r.start, end: r.end }));
    ws.on("decode", () => {
      regions.enableDragSelection({ color: "rgba(201, 119, 46, 0.25)" });
    });
    return () => { try { ws.destroy(); } catch (e) { console.debug("WaveSurfer teardown failed", e); } wsRef.current = null; };
  }, [loading, rec, audioUrl]);

  const speakers = useMemo(() => {
    const map = {};
    (rec?.utterances || []).forEach((u) => {
      if (u.end > u.start) {
        (map[u.speaker] = map[u.speaker] || []).push({ start: u.start / 1000, end: u.end / 1000 });
      }
    });
    return map;
  }, [rec]);

  const speakerName = (label) => rec?.speaker_map?.[label]?.name || (label && label.length <= 2 ? `Speaker ${label}` : label);

  const ensureDecoded = useCallback(async () => {
    if (decodedRef.current) return decodedRef.current;
    const ab = await decodeAudioFromUrl(audioUrl);
    decodedRef.current = toMono(ab);
    return decodedRef.current;
  }, [audioUrl]);

  const playPause = () => wsRef.current?.playPause();
  const playRegion = () => {
    const rs = regionsRef.current?.getRegions() || [];
    if (rs.length) rs[0].play();
  };

  const uploadClip = async (blob, meta) => {
    const fd = new FormData();
    fd.append("title", meta.title);
    fd.append("source", meta.source);
    if (meta.speaker_label) fd.append("speaker_label", meta.speaker_label);
    if (meta.start_ms != null) fd.append("start_ms", String(meta.start_ms));
    if (meta.end_ms != null) fd.append("end_ms", String(meta.end_ms));
    if (meta.duration_ms != null) fd.append("duration_ms", String(meta.duration_ms));
    fd.append("file", blob, `clip.${format}`);
    await api.post(`/recordings/${id}/clips`, fd);
    await loadData();
  };

  const saveTrim = async () => {
    if (!region) { toast.error("Drag on the waveform to select a region first"); return; }
    if (!title.trim()) { toast.error("Give the clip a title"); return; }
    setSaving(true);
    try {
      const { samples, sampleRate } = await ensureDecoded();
      const seg = sliceSamples(samples, sampleRate, region.start, region.end);
      const blob = exportClip(seg, sampleRate, format);
      await uploadClip(blob, {
        title: title.trim(), source: "trim",
        start_ms: Math.round(region.start * 1000), end_ms: Math.round(region.end * 1000),
        duration_ms: Math.round((region.end - region.start) * 1000),
      });
      toast.success("Clip saved");
      setTitle("");
    } catch (e) { toast.error("Could not create clip"); } finally { setSaving(false); }
  };

  const isolateSpeaker = async (label) => {
    const segs = speakers[label];
    if (!segs?.length) return;
    setSaving(true);
    try {
      const { samples, sampleRate } = await ensureDecoded();
      const seg = concatSegments(samples, sampleRate, segs);
      if (seg.length === 0) { toast.error("No matching audio for this speaker"); setSaving(false); return; }
      const blob = exportClip(seg, sampleRate, format);
      const dur = segs.reduce((s, x) => s + (x.end - x.start), 0);
      await uploadClip(blob, { title: `${speakerName(label)} — isolated`, source: "speaker", speaker_label: speakerName(label), duration_ms: Math.round(dur * 1000) });
      toast.success(`Isolated ${speakerName(label)}`);
    } catch (e) { toast.error("Could not isolate speaker"); } finally { setSaving(false); }
  };

  const setShare = async (clip, level) => {
    try {
      const r = await api.put(`/clips/${clip.clip_id}`, { share_level: level });
      setClips((prev) => prev.map((c) => c.clip_id === clip.clip_id ? r.data : c));
      toast.success(level === "public" ? "Clip is now public" : level === "members" ? "Shared with members" : "Set to Core only");
    } catch (e) { toast.error("Could not update sharing"); }
  };

  const copyPublicLink = (clip) => {
    const url = `${window.location.origin}/clip/${clip.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied");
  };

  const deleteClip = async (clip) => {
    try { await api.delete(`/clips/${clip.clip_id}`); setClips((prev) => prev.filter((c) => c.clip_id !== clip.clip_id)); toast.success("Clip deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  if (loading) return <SectionLoader />;
  if (!rec) return null;

  const speakerLabels = Object.keys(speakers);

  return (
    <div className="space-y-5">
      <div>
        <Link to={`/admin/recordings/${id}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to recording</Link>
        <h1 className="mt-1 font-serif text-2xl font-semibold text-foreground">Clip Editor · {rec.title}</h1>
        <p className="mt-1 text-muted-foreground">Trim a selection or isolate a single speaker, then export & share.</p>
      </div>

      {!rec.has_audio ? (
        <EmptyState icon={AudioLines} title="No audio to edit" description="This recording was created from a pasted transcript. Upload or record audio to use the clip editor." />
      ) : (
        <>
          {/* Waveform editor */}
          <Card className="border-border bg-card"><CardContent className="p-4">
            <div ref={containerRef} className="w-full" data-testid="clip-waveform" />
            {!ready && <p className="mt-3 text-center text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" />Loading waveform…</p>}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="secondary" onClick={playPause} data-testid="clip-playpause">{playing ? <Pause className="mr-1.5 h-4 w-4" /> : <Play className="mr-1.5 h-4 w-4" />}{playing ? "Pause" : "Play"}</Button>
              <Button size="sm" variant="ghost" onClick={playRegion} disabled={!region}>Play selection</Button>
              <span className="text-sm text-muted-foreground tabular-nums">{region ? `Selection: ${fmt(region.start)} – ${fmt(region.end)} (${fmt(region.end - region.start)})` : "Drag on the waveform to select a region"}</span>
            </div>
          </CardContent></Card>

          {/* Save controls */}
          <Card className="border-border bg-card"><CardContent className="p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
              <div><Label>Clip title</Label><Input className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Maria on follow-up" data-testid="clip-title-input" /></div>
              <div><Label>Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="mt-1.5 w-28" data-testid="clip-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="mp3">MP3</SelectItem><SelectItem value="wav">WAV</SelectItem></SelectContent>
                </Select>
              </div>
              <Button onClick={saveTrim} disabled={saving || !region} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="clip-save-trim"><Scissors className="mr-1.5 h-4 w-4" />{saving ? "Saving…" : "Save clip"}</Button>
            </div>
          </CardContent></Card>

          {/* Speaker isolation */}
          {speakerLabels.length > 0 && (
            <Card className="border-border bg-card"><CardContent className="p-4">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><UserRound className="h-4 w-4" />Isolate a speaker</p>
              <p className="text-xs text-muted-foreground">Builds a clip from just that speaker's parts using transcript timestamps{rec.is_sandbox ? " (sandbox timestamps — accurate once AssemblyAI is connected)" : ""}.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {speakerLabels.map((label) => (
                  <Button key={label} size="sm" variant="secondary" onClick={() => isolateSpeaker(label)} disabled={saving} data-testid="clip-isolate-speaker">
                    <Scissors className="mr-1.5 h-3.5 w-3.5" />{speakerName(label)}
                  </Button>
                ))}
              </div>
            </CardContent></Card>
          )}
        </>
      )}

      {/* Clips list */}
      <div>
        <h2 className="mb-3 font-serif text-lg font-semibold text-foreground">Saved clips ({clips.length})</h2>
        {clips.length === 0 ? (
          <EmptyState icon={Scissors} title="No clips yet" description="Create a clip from a selection or isolate a speaker above." />
        ) : (
          <div className="space-y-3">
            {clips.map((c) => {
              const Meta = SHARE_META[c.share_level] || SHARE_META.core;
              return (
                <Card key={c.clip_id} className="border-border bg-card" data-testid="clip-item">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-foreground">{c.title}</p>
                        <p className="text-xs text-muted-foreground">{c.source === "speaker" ? `Speaker · ${c.speaker_label}` : "Trim"} · {(c.audio_ext || "").toUpperCase()}</p>
                      </div>
                      <Badge variant="secondary" className="gap-1"><Meta.icon className="h-3 w-3" />{Meta.label}</Badge>
                    </div>
                    <audio controls className="mt-3 w-full" src={mediaUrl(`${API_BASE}/clips/${c.clip_id}/audio`, ticket) || undefined} data-testid="clip-audio" />
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button asChild size="sm" variant="secondary"><a href={mediaUrl(`${API_BASE}/clips/${c.clip_id}/audio`, ticket) || "#"} download><Download className="mr-1.5 h-3.5 w-3.5" />Download</a></Button>
                      <Select value={c.share_level} onValueChange={(v) => setShare(c, v)}>
                        <SelectTrigger className="h-8 w-36" data-testid="clip-share-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="core">Core only</SelectItem>
                          <SelectItem value="members">Share with members</SelectItem>
                          <SelectItem value="public">Public link</SelectItem>
                        </SelectContent>
                      </Select>
                      {c.share_level === "public" && c.public_token && (
                        <Button size="sm" variant="ghost" onClick={() => copyPublicLink(c)} data-testid="clip-copy-link"><Link2 className="mr-1.5 h-3.5 w-3.5" />Copy link</Button>
                      )}
                      <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => deleteClip(c)} data-testid="clip-delete"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
